define('home/mixins/withExpiration',[
    'underscore',
    'modernizr',
], function (
    _,
    Modernizr
) {
    'use strict';

    var localStorageSupported = Modernizr.localstorage;

    /**
     * Mixin for views that are meant to be shown X number of times or until it has
     * been closed. Given a string unique to this view with which to store the
     * number of times it has been viewed, and optionally the number of times the view
     * should show before hiding. (Default is 5)
     *
     * Javascript:
     *     withExpiration.call(View.prototype, 'notificationOnboardViewsRemaining')
     *         OR
     *     withExpiration.call(View.prototype, 'notificationOnboardViewsRemaining', 10)
     *
     * viewExpired is passed into the template to show or hide the view.
     *
     * Template:
     *    {{#unless viewExpired}}
     *        <div>
     *            <div> some content </div>
     *
     *            {{! hides view permanently when clicked }}
     *            <a href="#" class="close" data-action="force-expiration"><span class="icon-cancel"></span></a>
     *        </div>
     *    {{/unless}}
     *
     */
    var ExpirationMixin = {
        templateHelpers: function (_templateHelpers) {
            if (_.isFunction(_templateHelpers))
                _templateHelpers = _templateHelpers.call(this);

            return _.extend({
                // Hide the view if the local storage value indicates not
                // to show the view anymore
                viewExpired: !localStorageSupported || this.getRemainingViews() <= 0,
            }, _templateHelpers);
        },

        events: {
            'click [data-action=force-expiration]': 'forceExpire',
        },

        onShow: function (_onShow) {
            if (_.isFunction(_onShow))
                _onShow.call(this);

            this.decrementRemainingViews();
        },

        /**
         * Updates local storage to indicate that this user should not
         * see the view ever again.
         */
        forceExpire: function (evt) {
            if (evt)
                evt.preventDefault();
            this.setRemainingViews(-1);
            this.remove();
        },

        /**
         * At most once per app load, decrements the counter for how many times the
         * user has left to see the view
         */
        decrementRemainingViews: _.once(function () {
            var toSeeCount = this.getRemainingViews();

            if (toSeeCount > 0)
                this.setRemainingViews(toSeeCount - 1);
        }),

        /**
         * Reads the local storage tracking how many times this user has
         * left to see the view.
         * If value does not exist, or if local storage not supported, returns
         * the default number of views.
         * @returns {integer}
         */
        getRemainingViews: function () {
            if (localStorageSupported) {
                var remainingViews = parseInt(window.localStorage.getItem(this.getExpirationKey()), 10);
                if (!isNaN(remainingViews))
                    return remainingViews;
            }

            return this.getNumberOfViews();
        },

        /**
         * Returns how many times this view should be shown before being hidden.
         * Defaults to 5.
         */
        getNumberOfViews: function () {
            return this.defaultViews || 5;
        },

        /**
         * Writes to local storage tracking how many times this user has
         * left to see the view.
         * Does nothing if local storage is not supported
         * @param  {integer} value the new value
         */
        setRemainingViews: function (value) {
            if (localStorageSupported)
                window.localStorage.setItem(this.getExpirationKey(), value);
        },

        getExpirationKey: function () {
            return _.result(this, 'expirationKey');
        },
    };

    return function withExpiration() {
        this.templateHelpers = _.wrap(this.templateHelpers, ExpirationMixin.templateHelpers);
        this.onShow = _.wrap(this.onShow, ExpirationMixin.onShow);
        this.events = _.defaults({}, this.events, ExpirationMixin.events);

        _.extend(this, _.pick(ExpirationMixin,
            'forceExpire',
            'decrementRemainingViews',
            'getRemainingViews',
            'getNumberOfViews',
            'setRemainingViews',
            'getExpirationKey'
        ));
    };
});

